import { useLazyQuery, useQuery } from '@apollo/client/react';
import { useEffect } from 'react';

import {
  BroadbandSwitchingMatchRequestInput,
  GetOtsMatchQuery,
} from '@generated/gql/graphql';
import {
  IOTSAddress,
  IOTSProvider,
  OTSMatchStatus,
  OTSServiceAction,
  OrderType,
} from '@src/types/oneTouchSwitching';
import { getOtsMatch, otsMatch } from '@utils/queries/otsQuery';

interface UseOtsMatchProps {
  selectedProvider: IOTSProvider | null;
  orderType: OrderType;
  currentAddress: IOTSAddress;
  numberToPort: string | null;
  surname: string;
  accountReference: string;
  broadbandReference: string;
  homePhoneReference: string | null;
  matchId: string | null;
  canUpdateState: boolean;

  onMatchCreated: (matchId: string) => void;
  onSuccess: (matchData: GetOtsMatchQuery) => void;
  onNotFound: (matchData: GetOtsMatchQuery) => void;
  onFail: () => void;
}
const useOtsMatch = ({
  selectedProvider,
  orderType,
  currentAddress,
  numberToPort,
  surname,
  accountReference,
  broadbandReference,
  homePhoneReference,
  matchId,
  canUpdateState,
  onSuccess,
  onNotFound,
  onFail,
  onMatchCreated,
}: UseOtsMatchProps) => {
  const [executeMatch, { data, error: matchError, refetch, called }] =
    useLazyQuery(otsMatch);

  const {
    data: matchData,
    error: matchPollError,
    stopPolling,
  } = useQuery(getOtsMatch, {
    skip: !matchId || !canUpdateState,
    variables: {
      request: {
        id: matchId ?? '',
      },
    },
    pollInterval: 1000,
  });

  const findDetails = (updatedSurname?: string) => {
    if (!canUpdateState || !selectedProvider) return;

    const request: BroadbandSwitchingMatchRequestInput = {
      broadband: {
        action: OTSServiceAction.Cease,
        identifier: broadbandReference,
      },
      provider: selectedProvider.id,
      surname: updatedSurname ?? surname,
      accountReference,
      address: currentAddress,
    };

    if (orderType === OrderType.BROADBAND_AND_VOICE) {
      request.homePhone = {
        action: numberToPort ? OTSServiceAction.Port : OTSServiceAction.Cease,
        identifier: homePhoneReference || numberToPort || undefined,
      };
    }

    if (called) {
      refetch({ request });
    } else {
      executeMatch({ variables: { request } });
    }
  };

  useEffect(() => {
    if (!canUpdateState) return;

    const status = matchData?.broadbandOtsRetrieveMatch.status;
    if (matchError || matchPollError) {
      stopPolling();
      return onFail();
    }
    if (!matchData || status === OTSMatchStatus.Pending) return;
    stopPolling();

    switch (status) {
      case OTSMatchStatus.ErrorNoMatch:
        onNotFound(matchData);
        break;
      case OTSMatchStatus.Success:
        onSuccess(matchData);
        break;
      case OTSMatchStatus.ErrorComplexResponseType:
      case OTSMatchStatus.ErrorNotSwitch:
      case OTSMatchStatus.ErrorNoReply:
        onFail();
        break;
    }
  }, [
    matchData,
    matchError,
    matchPollError,
    stopPolling,
    canUpdateState,
    onNotFound,
    onSuccess,
    onFail,
  ]);
  useEffect(() => {
    if (data?.broadbandOtsMatch.id && canUpdateState) {
      onMatchCreated(data.broadbandOtsMatch.id);
    }
  }, [data, canUpdateState, onMatchCreated]);
  return { findDetails };
};

export { useOtsMatch };
